import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Annoying-by-design check-in reminder.
// - Window: 09:30 → 11:30 local. Pings every 5 min if not checked in.
// - Beep + toast + tab title flash.
// - Phase 82: the toast NO LONGER contains a quick-check-in action button —
//   users must click the Check-in button on the dashboard themselves.
//   Rationale: the toast action could fire on accidental tap / keyboard
//   focus, producing the "auto check-in without click" reports.
// - Phase 64: silent on Saturdays, Sundays, and any date in the holiday
//   calendar (`/api/leaves/holidays`). We cache the holiday set client-side
//   and refresh once per session so a freshly-added holiday picks up next
//   reload — which matches user expectations for an annoying nudge.

function safeBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    o.type = "square";
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch (err) {
    // Browsers can block audio before user interaction — log once and move on.
    logger.warn("Reminder beep blocked:", err?.message || err);
  }
}

async function doCheckInFromToast() {
  // Phase 82 — retired. Kept as a no-op to keep the module import graph
  // stable while any legacy toast-action references outside this file get
  // cleaned up. Users must now click the dashboard Check-in button.
}

export default function CheckInReminder() {
  const { user } = useAuth();
  const titleRef = useRef(document.title);
  const [holidaySet, setHolidaySet] = useState(null); // Set<"YYYY-MM-DD"> | null while loading

  // Hydrate the holiday set once per session. Failure here is non-fatal — we
  // just skip the weekend/holiday gate and behave like before.
  useEffect(() => {
    if (!user || user === false || user.role === "client") return;
    let cancelled = false;
    api.get("/leaves/holidays", { params: { year: new Date().getFullYear() } })
      .then(({ data }) => {
        if (cancelled) return;
        setHolidaySet(new Set((data || []).map((h) => h.date)));
      })
      .catch((err) => {
        if (!cancelled) {
          logger.warn("Holiday list fetch failed (reminder will fire on all weekdays):", err?.message || err);
          setHolidaySet(new Set()); // sentinel so the gate becomes weekend-only
        }
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user || user === false || user.role === "client") return;
    // Hold the loop until the holiday hydrate resolves — prevents a spurious
    // 09:30 ping on a holiday during the first 5s after page load.
    if (holidaySet === null) return;
    let cancelled = false;

    function isOffDay(now) {
      const dow = now.getDay(); // 0=Sun .. 6=Sat
      if (dow === 0 || dow === 6) return true;
      const isoLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      return holidaySet.has(isoLocal);
    }

    // Office hours (09:30–11:30) are always IST — read via Intl instead of
    // now.getHours()/getMinutes() so a mis-set device timezone can't push
    // the nudge to the wrong wall-clock time.
    function istMinutesOfDay(now) {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(now);
      const hh = Number(parts.find((p) => p.type === "hour")?.value || 0);
      const mm = Number(parts.find((p) => p.type === "minute")?.value || 0);
      return hh * 60 + mm;
    }

    async function ping() {
      if (cancelled) return;
      const now = new Date();
      if (isOffDay(now)) return;
      const minutesOfDay = istMinutesOfDay(now);
      if (minutesOfDay < 9 * 60 + 30 || minutesOfDay > 11 * 60 + 30) return;
      try {
        const { data } = await api.get("/attendance/today");
        if (data && data.check_in) return; // already in, do nothing
        safeBeep();
        toast.error("👋 Check in already! It's past 9:30 — head to Home to check in.", {
          duration: 6000,
          position: "top-right",
        });
        let on = false;
        const flash = setInterval(() => {
          document.title = on ? titleRef.current : "🟠 Check in!";
          on = !on;
        }, 800);
        setTimeout(() => { clearInterval(flash); document.title = titleRef.current; }, 4500);
      } catch (err) {
        // Network glitch — quietly log so we don't spam the user with errors during the polling loop.
        logger.warn("Check-in reminder poll failed:", err?.message || err);
      }
    }

    const initial = setTimeout(ping, 5000);
    const id = setInterval(ping, 5 * 60 * 1000);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(id); };
  }, [user, holidaySet]);

  return null;
}
